/**
 * Anular lo que se emitió por error: una receta, unos exámenes, un plan.
 *
 * POR QUÉ ANULAR Y NO EDITAR
 *
 * Una receta emitida ya se imprimió y el paciente puede llevarla en la mano
 * camino de la farmacia. Cambiarle los medicamentos por debajo dejaría dos
 * documentos distintos con el mismo número: el papel que tiene el paciente y
 * lo que ve quien despacha. Se anula —queda el registro de que existió y por
 * qué se retiró— y se emite una nueva. Lo mismo con los exámenes: laboratorio
 * ya los tiene en su cola.
 *
 * POR QUÉ SE PIDE MOTIVO
 *
 * Porque es historia clínica. Dentro de un mes, "Receta #40 anulada" sin más
 * no le dice nada a nadie; "me equivoqué de dosis" o "el paciente es alérgico"
 * explica qué pasó. El backend lo exige, y hace bien.
 */
import { useState } from "react";
import { Button, Dialog, Field } from "../ui";

export function AnularDialog({
  open,
  onClose,
  onConfirm,
  titulo,
  queSeAnula,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (motivo: string) => void;
  titulo: string;
  /** Qué exactamente se va a anular: "Receta #12", "Orden #4". */
  queSeAnula: string;
  loading?: boolean;
  error?: string | null;
}) {
  const [motivo, setMotivo] = useState("");

  return (
    <Dialog open={open} onClose={onClose} title={titulo} size="sm">
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {queSeAnula}
        </div>
        <p className="text-sm text-slate-600">
          Queda anulada y registrada como tal. Después puedes emitir una nueva.
        </p>
        <Field
          label="Motivo"
          htmlFor="motivo-anular"
          hint="Queda en la historia. Por ejemplo: dosis equivocada, el paciente es alérgico."
        >
          <textarea
            id="motivo-anular"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            autoFocus
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
          />
        </Field>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            No, volver
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={loading}
            // Sin motivo no se anula: el backend lo rechazaría igual, y
            // enterarse después de darle al botón es peor.
            disabled={motivo.trim().length === 0}
            onClick={() => onConfirm(motivo.trim())}
          >
            Sí, anular
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
